import {Action, openDeleteDialog} from 'platform/components';

import {matchPath, useLocation} from 'react-router-dom';

import {isNil} from 'ramda';
import {isNotNil} from 'ramda-adjunct';

import {useChangeReturnVehicleInspectionMutation, useGetRentalOrderQuery} from '@dms/api/rental';
import i18n from '@dms/i18n';
import {rentalOrderRoutes} from '@dms/routes';
import {handleApiError} from '@dms/shared';
import {NewInspectionButton} from '@dms/teas';

import {buildArray, composePath, isNotNilOrEmpty, useRequiredParams} from 'shared/web';

export const useReturnInspectionActions = (): Action[] => {
  const {id: rentalOrderId} = useRequiredParams();
  const {data: rentalOrder} = useGetRentalOrderQuery({rentalOrderId});
  const [updateReturnInspection] = useChangeReturnVehicleInspectionMutation();
  const {pathname} = useLocation();

  const vehicleId = rentalOrder?.rentalVehicleId;
  const hasReturnInspection = isNotNilOrEmpty(rentalOrder?.returnVehicleInspections);

  const inspectionPath = composePath(rentalOrderRoutes.returnVehicleInspections, {
    params: {id: rentalOrderId},
  });

  const shouldShowNewInspectionButton = isNotNil(matchPath(pathname, inspectionPath.split('?')[0]));

  const handleUpdateReturnInspection = async (inspectionId: string) => {
    await updateReturnInspection({
      rentalOrderId,
      changeReturnVehicleInspectionRequestBody: {
        inspections: [inspectionId],
      },
    })
      .unwrap()
      .catch(handleApiError);
  };

  const handleRemoveReturnInspection = () => {
    openDeleteDialog({
      text: i18n.t('page.rentalOrder.labels.removeInspectionDescription'),
      onConfirm: async () =>
        await updateReturnInspection({
          rentalOrderId,
          changeReturnVehicleInspectionRequestBody: {
            inspections: [],
          },
        })
          .unwrap()
          .catch(handleApiError),
    });
  };

  if (isNil(rentalOrderId) || isNil(vehicleId) || !shouldShowNewInspectionButton) {
    return [];
  }

  return buildArray<Action>()
    .when(hasReturnInspection, {
      type: 'button',
      title: i18n.t('page.rentalOrder.labels.removeInspection'),
      onClick: handleRemoveReturnInspection,
      variant: 'danger',
    })
    .whenNot(hasReturnInspection, {
      type: 'custom',
      component: (
        <NewInspectionButton
          wrapperBoxProps={{paddingBottom: 0}}
          vehicleId={vehicleId ?? ''}
          onAfterSubmit={handleUpdateReturnInspection}
          getRedirectRoute={() =>
            composePath(rentalOrderRoutes.returnVehicleInspections, {
              params: {
                id: rentalOrderId,
              },
            })
          }
        />
      ),
    });
};
